class ActivityTracker {
  constructor(options = {}) {
    this.heartbeatInterval = options.heartbeatInterval || 1000 * 60 * 5; // 5 minutes
    this.warningBuffer = options.warningBuffer || 60;
    this.expirationModalBuffer = options.expirationModalBuffer || 60;
    this.heartbeatEndpoint = '/api/v1/sessions/heartbeat';
    this.lastActivityTime = Date.now()
    this.heartbeatTimer = null;
    this.isActive = true;
    this.isPaused = false;
    this.pageContext = options.pageContext || 'general';
    this.pendingHeartbeat = null;
    this.timeoutBannerEnabled = options.timeoutBannerEnabled !== undefined ? options.timeoutBannerEnabled : (window.timeoutBannerEnabled || false);

    this.init();
  }

  pause() {
    this.isPaused = true;
  }

  resume() {
    this.isPaused = false;
  }

  init() {
    this.bindActivityEvents();
    this.startAdaptiveHeartbeat();
  }

  bindActivityEvents() {
    this.boundRecordActivity = (e) => {
      this.recordActivity(e);
    };

    $(document).on('click keypress', this.boundRecordActivity);
  }

  recordActivity(event = null) {
    if (!this.isActive || this.isPaused) return;

    this.lastActivityTime = Date.now();

    if (typeof window.tokenExpiresIn !== 'undefined' && window.tokenExpiresIn < this.warningBuffer) {
      this.sendHeartbeat('activity_near_expiration');
    }
  }

  startAdaptiveHeartbeat() {
    const schedule = () => {
      clearTimeout(this.heartbeatTimer);
      const leftMs = (window.tokenExpiresIn ?? 0) * 1_000;
      const modalBufferMs = this.expirationModalBuffer * 1_000;
      const msUntilModalBuffer = leftMs - modalBufferMs;

      const nextMs = Math.max(10_000, Math.min(
        this.heartbeatInterval,
        leftMs / 2,
        msUntilModalBuffer > 0 ? msUntilModalBuffer : Infinity
      ));

      this.heartbeatTimer = setTimeout(async () => {
        if (this.shouldShowWarningModal()) {
          this.showWarningModal();
        }

        if (this.isActive && !this.isPaused && window.tokenExpiresIn > this.warningBuffer) {
          await this.sendHeartbeat('adaptive');
        }
        schedule();
      }, nextMs);
    };

    schedule();
  }

  shouldShowWarningModal() {
    return this.timeoutBannerEnabled &&
           !this.isPaused &&
           window.tokenExpiresIn <= this.expirationModalBuffer &&
           window.tokenExpiresIn > 0;
  }

  showWarningModal() {
    if (typeof SessionTimeoutWarningModal !== 'undefined' &&
        !SessionTimeoutWarningModal.getStatus().isVisible) {
      SessionTimeoutWarningModal.show(window.tokenExpiresIn);
      this.pause();
    }
  }

  async sendHeartbeat(trigger = 'manual', force = false) {
    if (!force && (this.pendingHeartbeat || this.isPaused)) {
      return Promise.resolve();
    }

    const metaElement = document.querySelector('meta[name="csrf-token"]');
    const xcsrfToken = metaElement ? metaElement.content : '';
    const secondsSinceActivity = Math.floor((Date.now() - this.lastActivityTime) / 1000);

    const headers = {
      'X-CSRF-Token': xcsrfToken,
      'Content-Type': 'application/json'
    };

    const body = JSON.stringify({
      active_ago: secondsSinceActivity,
      page_context: this.pageContext,
      trigger: trigger
    });

    try {
      this.pendingHeartbeat = fetch(this.heartbeatEndpoint, {
        method: 'POST',
        headers,
        body
      });
      const response = await this.pendingHeartbeat;

      if (response.ok) {
        const data = await response.json();
        this.updateSessionTimerFromServerResponse(data);
        return Promise.resolve();
      } else {
        console.error(`[ActivityTracker] Heartbeat failed (${trigger}):`, response.status, response.statusText);
        this.handleHeartbeatError({ status: response.status }, trigger);
        return Promise.reject(response.status);
      }
    } catch (error) {
      console.error(`[ActivityTracker] Heartbeat request failed (${trigger}):`, error);
      this.handleHeartbeatError({ status: 500 }, trigger);
      return Promise.reject(500);
    } finally {
      this.pendingHeartbeat = null;
    }
  }

  updateSessionTimerFromServerResponse(response) {
    if (typeof window.tokenExpiresIn !== 'undefined') {
      const expiresAtDate = new Date(response.expires_at);
      const serverTimeDate = new Date(response.server_time);
      const remainingSeconds = Math.floor((expiresAtDate - serverTimeDate) / 1000);

      window.tokenExpiresIn = Math.max(0, remainingSeconds);
    }
  }

  handleHeartbeatError(xhr, trigger) {
    console.error(`[ActivityTracker] Heartbeat failed (${trigger}):`, xhr.status, xhr.statusText);
    
    if (xhr.status === 401) {
      window.location = '/timeout?logout=true'
    } else if (xhr.status >= 500) {
      console.warn('[ActivityTracker] Server error, will retry on next heartbeat');
    }
  }

  stop() {
    console.log(`[ActivityTracker] Stopping tracker for ${this.pageContext}`);

    this.isActive = false;

    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    if (this.pendingHeartbeat) {
      this.pendingHeartbeat = null;
    }

    if (this.boundRecordActivity) {
      $(document).off('click keypress', this.boundRecordActivity);
      this.boundRecordActivity = null;
    }

    console.log(`[ActivityTracker] Tracker stopped for ${this.pageContext}`);
  }

  getStatus() {
    const secondsSinceActivity = Math.floor((Date.now() - this.lastActivityTime) / 1000);
    const sessionTimeRemaining = window.tokenExpiresIn || 0;

    return {
      isActive: this.isActive,
      isPaused: this.isPaused,
      lastActivityTime: this.lastActivityTime,
      secondsSinceActivity: secondsSinceActivity,
      sessionTimeRemaining: sessionTimeRemaining,
      warningBuffer: this.warningBuffer,
      expirationModalBuffer: this.expirationModalBuffer,
      pageContext: this.pageContext
    };
  }
}

window.ActivityTracker = ActivityTracker;
